import { api } from '../../lib/api';
import type {
  ActorKind,
  AiStatusDto,
  ApiTokenDto,
  CreatedApiToken,
  Page,
  SettingDto,
  WebSessionDto,
} from '../../lib/dto';

/**
 * سطح REST لشاشة الإعدادات (backend-api §7.1 و§7.8). كلها مسارات **cookie فقط**:
 * «لا رمز يصنع رمزًا» — رمز مسروق لا يُصدر بديلًا لنفسه ولا يبطل غيره، فيبقى
 * الإبطال من هذه الشاشة هو زر الإيقاف الفعلي (قرارا D7/O-1).
 *
 * أشكال الأغلفة مختلفة بين المسارات عن قصد وليست خطأ نسخ: `/settings` مصفوفة
 * عارية، و`/tokens` غلاف `{ items }` بلا cursor، و`/auth/sessions` غلاف الصفحة
 * الكامل بـ `next_cursor: null`. الأنواع أدناه تطابق مخططات الخادم حرفًا بحرف.
 */

/* ── الإعدادات ─────────────────────────────────────────────────────────────── */

export function fetchSettings(): Promise<readonly SettingDto[]> {
  return api.get<readonly SettingDto[]>('/settings');
}

export interface UpdateSettingResponse {
  readonly setting: SettingDto;
}

/** `PUT /settings/:key` — الجسم `{ value }`، والمفتاح في المسار (لا في الجسم). */
export function putSetting(key: string, value: unknown): Promise<UpdateSettingResponse> {
  return api.put<UpdateSettingResponse>(`/settings/${encodeURIComponent(key)}`, { value });
}

/**
 * `GET /ai/status` — تشخيص طبقة الذكاء. **قراءة خالصة بلا معامِلات**: كل ما تحتاجه
 * الشاشة يأتي في نداءٍ واحد كي لا تُركَّب الحالة من مصدرين قد يفترقان لحظةً.
 */
export function fetchAiStatus(): Promise<AiStatusDto> {
  return api.get<AiStatusDto>('/ai/status');
}

/* ── الجلسات (قرار D55) ────────────────────────────────────────────────────── */

export function fetchWebSessions(): Promise<Page<WebSessionDto>> {
  return api.get<Page<WebSessionDto>>('/auth/sessions');
}

export function revokeWebSession(sessionId: string): Promise<{ readonly ok: true }> {
  return api.post<{ readonly ok: true }>(`/auth/sessions/${encodeURIComponent(sessionId)}/revoke`);
}

/* ── الرموز (قرار D7) ──────────────────────────────────────────────────────── */

export interface ApiTokenList {
  readonly items: readonly ApiTokenDto[];
}

export function fetchApiTokens(): Promise<ApiTokenList> {
  return api.get<ApiTokenList>('/tokens');
}

export interface CreateTokenInput {
  readonly name: string;
  readonly actor_kind: ActorKind;
  /** `null` = كل الأدوات؛ قائمة = تقييد يشمل القراءة والكتابة معًا (قرارا D2/D39). */
  readonly allowed_tools: readonly string[] | null;
  readonly expires_in_days?: number;
}

/** يعيد السر **مرة واحدة**؛ لا يُخزَّن في كاش الاستعلام ولا في أي تخزين (قرار D7). */
export function createApiToken(input: CreateTokenInput): Promise<CreatedApiToken> {
  return api.post<CreatedApiToken>('/tokens', input);
}

export function revokeApiToken(tokenId: string): Promise<{ readonly revoked: true }> {
  return api.post<{ readonly revoked: true }>(`/tokens/${encodeURIComponent(tokenId)}/revoke`);
}
